import { AudioStorage } from "./AudioStorage";
import { WaveformData } from "./types";
import { calculateWaveform } from "./calculateWaveform";

/**
 * Input data for loading a region from storage.
 */
export interface LegacyRegionLoadInput {
  id: string;
  audioBlob?: Blob;
  key: string;
  waveformKey: string | null;
}

/**
 * Result of loading a single region via the legacy loader.
 */
export interface LegacyRegionLoadResult {
  id: string;
  audioBlob: Blob;
  audioBuffer: AudioBuffer;
  duration: number;
  waveform: WaveformData;
}

/**
 * Region loader for the legacy (AudioBuffer-based) playback backend.
 *
 * Every region is fully decoded via `decodeAudioData()` to produce an
 * `AudioBuffer` for both playback and waveform calculation.
 *
 * When loading multiple regions via `loadAll()`, the progress callback
 * is debounced internally via `queueMicrotask` so that many regions
 * resolving on the same tick produce a single callback invocation.
 */
export class LegacyRegionLoader {
  private audioContext: AudioContext;
  private audioStorage: AudioStorage;

  constructor(audioContext: AudioContext, audioStorage: AudioStorage) {
    this.audioContext = audioContext;
    this.audioStorage = audioStorage;
  }

  async loadFromKey(
    id: string,
    key: string,
    waveformKey: string | null,
    audioBlob?: Blob,
  ): Promise<LegacyRegionLoadResult> {
    const blob = audioBlob ?? await this.audioStorage.getAudio(id, key);
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

    const waveform = await this.audioStorage.getWaveform(
      id,
      waveformKey,
      () => calculateWaveform(audioBuffer),
    );

    return { audioBlob: blob, id, audioBuffer, duration: audioBuffer.duration, waveform };
  }

  async loadAll(
    regions: LegacyRegionLoadInput[],
    onProgress?: (loaded: number, total: number) => void,
    onError?: (regionId: string, error: Error) => void,
  ): Promise<LegacyRegionLoadResult[]> {
    const total = regions.length;
    let loaded = 0;
    let progressScheduled = false;

    const notifyProgress = () => {
      if (onProgress && !progressScheduled) {
        progressScheduled = true;
        queueMicrotask(() => {
          progressScheduled = false;
          onProgress(loaded, total);
        });
      }
    };

    const settled = await Promise.allSettled(
      regions.map(async (region) => {
        const result = await this.loadFromKey(
          region.id,
          region.key,
          region.waveformKey,
          region.audioBlob,
        );
        loaded++;
        notifyProgress();
        return result;
      })
    );

    const results: LegacyRegionLoadResult[] = [];
    for (let i = 0; i < settled.length; i++) {
      const outcome = settled[i];
      if (outcome.status === "fulfilled") {
        results.push(outcome.value);
      } else {
        const error = outcome.reason instanceof Error
          ? outcome.reason
          : new Error(String(outcome.reason));
        onError?.(regions[i].id, error);
        loaded++;
        notifyProgress();
      }
    }

    return results;
  }

  /**
   * Generate waveform data from an existing AudioBuffer.
   * Used for recordings and punch-in operations where the buffer already exists.
   */
  static calculateWaveform(audioBuffer: AudioBuffer): WaveformData {
    return calculateWaveform(audioBuffer);
  }
}
